import React, { useLayoutEffect } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  type Placement,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Box } from '@mui/material';
import { motion } from 'motion/react';

import { HOVER_POPOVER_REST_MS, type HoverPopoverAnchorReference } from './HoverPopover.types';

interface HoverPopoverSurfaceProps {
  open: boolean;
  /** Where to anchor — a live element reference or a captured-rect fallback. */
  anchorReference: HoverPopoverAnchorReference;
  /** floating-ui asked to close (escape / outside-press). */
  onClose: () => void;
  /** Pointer entered the floating card — callers cancel any pending close. */
  onPointerEnter?: () => void;
  /** Pointer left the floating card — callers schedule a close. */
  onPointerLeave?: () => void;
  /**
   * A press happened inside the card. Callers that allow interaction (editing,
   * buttons) use this to "pin" the popover open until explicitly dismissed.
   */
  onInteractionPin?: () => void;
  placement?: Placement;
  offsetPx?: number;
  fallbackPlacements?: Placement[];
  /**
   * floating-ui outside-press predicate: return true to allow the press to
   * dismiss. Defaults to always-dismiss.
   */
  outsidePress?: (event: MouseEvent) => boolean;
  role?: 'dialog' | 'tooltip';
  zIndex?: number;
  /**
   * Stop pointer/click events inside the card from bubbling to whatever sits
   * behind it (calendar grid, drill-in link, …). Default true.
   */
  stopPropagation?: boolean;
  children: React.ReactNode;
}

/**
 * The positioned, portaled, animated floating container — the reusable core of
 * the calendar's hover card. It owns floating-ui placement + dismissal only;
 * open/close *intent* (hover timers, which anchor is active) lives in the
 * caller via useHoverIntent / useKeyedHoverPopover. Render any content as
 * `children`.
 */
export const HoverPopoverSurface = ({
  open,
  anchorReference,
  onClose,
  onPointerEnter,
  onPointerLeave,
  onInteractionPin,
  placement = 'right-start',
  offsetPx = 12,
  fallbackPlacements = ['left-start', 'bottom-start', 'top-start'],
  outsidePress,
  role = 'dialog',
  zIndex = 1000,
  stopPropagation = true,
  children,
}: HoverPopoverSurfaceProps) => {
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: nextOpen => {
      if (!nextOpen) onClose();
    },
    placement,
    transform: false,
    middleware: [
      offset(offsetPx),
      flip({ fallbackPlacements }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: outsidePress ?? true,
  });
  const roleInteraction = useRole(context, { role });
  const { getFloatingProps } = useInteractions([dismiss, roleInteraction]);

  useLayoutEffect(() => {
    refs.setPositionReference(anchorReference);
  }, [anchorReference, refs]);

  if (!open) return null;

  const stop = (event: React.SyntheticEvent) => {
    if (stopPropagation) event.stopPropagation();
  };

  const pin = (event: React.SyntheticEvent) => {
    if (stopPropagation) event.stopPropagation();
    onInteractionPin?.();
  };

  return (
    <FloatingPortal>
      <Box
        component={motion.div}
        ref={refs.setFloating}
        style={{ ...floatingStyles, zIndex }}
        initial={false}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: HOVER_POPOVER_REST_MS / 1000 }}
        {...getFloatingProps({
          onClick: stop,
          onMouseEnter: () => onPointerEnter?.(),
          onMouseLeave: () => onPointerLeave?.(),
          onMouseDown: pin,
          onMouseUp: stop,
          onPointerDown: pin,
          onPointerUp: stop,
          onTouchStart: pin,
          onTouchEnd: stop,
        })}
      >
        {children}
      </Box>
    </FloatingPortal>
  );
};

export type { HoverPopoverSurfaceProps };
